import { Link } from 'react-router-dom';
import {
  HouseholdMinistering,
  MinisteringPersonRef,
  PersonAsMinister,
  PersonMinistering,
  PersonMinisteredTo,
  useHouseholdMinistering,
  usePersonMinistering,
} from '../api';

/** Ministering panel on the person detail page — shows both directions. */
export function PersonMinisteringPanel({ personId }: { personId: string }) {
  const { data, isLoading, error } = usePersonMinistering(personId);
  if (isLoading) return <PanelShell><p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">Loading…</p></PanelShell>;
  if (error)     return <PanelShell><p className="px-3 py-2 text-sm text-rose-600 dark:text-rose-400">Error loading ministering.</p></PanelShell>;
  if (!data)     return null;

  const { as_minister, as_ministered_to } = data;
  if (as_minister.length === 0 && as_ministered_to.length === 0) {
    return (
      <PanelShell>
        <p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No ministering assignments.</p>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      {as_minister.length > 0 && (
        <div className="border-b border-slate-100 dark:border-slate-800">
          <SubHeader>Ministers to</SubHeader>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {as_minister.map((c) => <AsMinisterRow key={c.companionship_id} c={c} />)}
          </ul>
        </div>
      )}
      {as_ministered_to.length > 0 && (
        <div>
          <SubHeader>Ministered to by</SubHeader>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {as_ministered_to.map((a) => <MinisteredToRow key={a.assignment_id} a={a} />)}
          </ul>
        </div>
      )}
    </PanelShell>
  );
}

/** Ministering panel on the household detail page — only EQ assignments. */
export function HouseholdMinisteringPanel({ householdId }: { householdId: string }) {
  const { data, isLoading, error } = useHouseholdMinistering(householdId);
  if (isLoading) return <PanelShell><p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">Loading…</p></PanelShell>;
  if (error)     return <PanelShell><p className="px-3 py-2 text-sm text-rose-600 dark:text-rose-400">Error loading ministering.</p></PanelShell>;
  if (!data)     return null;
  if (data.assignments.length === 0) {
    return (
      <PanelShell>
        <p className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No ministers assigned.</p>
      </PanelShell>
    );
  }
  return (
    <PanelShell>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {data.assignments.map((a: HouseholdMinistering['assignments'][number]) => (
          <li key={a.assignment_id} className="px-3 py-2 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-slate-800 dark:text-slate-200">
                {ministersInline(a.ministers)}
              </span>
              <DateRange started={a.started_on} ended={a.ended_on} />
            </div>
          </li>
        ))}
      </ul>
    </PanelShell>
  );
}

// ─── Building blocks ────────────────────────────────────────────────────────

function PanelShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <div className="border-b border-slate-200 dark:border-slate-700 px-3 py-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Ministering
      </div>
      {children}
    </div>
  );
}

function SubHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2 text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
      {children}
    </div>
  );
}

function AsMinisterRow({ c }: { c: PersonAsMinister }) {
  const targets =
    c.organization === 'eq'
      ? c.assigned_households.map((h) => (
          <Link key={h.household_id} to={`/households/${h.household_id}`} className="hover:underline">
            {h.name_directory ?? '(unnamed household)'}
          </Link>
        ))
      : c.assigned_sisters.map((s) => (
          <Link key={s.person_id} to={`/people/${s.person_id}`} className="hover:underline">
            {s.name_full ?? '(unnamed)'}
          </Link>
        ));
  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{c.organization.toUpperCase()}</span>
        <DateRange started={c.started_on} ended={c.ended_on} />
      </div>
      {c.co_ministers.length > 0 && (
        <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
          with {personRefsInline(c.co_ministers)}
        </div>
      )}
      {targets.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-x-2 text-sm text-slate-700 dark:text-slate-300">
          {targets.flatMap((t, i) =>
            i === 0 ? [t] : [<span key={`s${i}`} className="text-slate-300 dark:text-slate-600">·</span>, t],
          )}
        </div>
      ) : (
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">No assignments yet.</p>
      )}
    </li>
  );
}

function MinisteredToRow({ a }: { a: PersonMinisteredTo }) {
  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex items-baseline justify-between">
        <span className="font-medium text-slate-800 dark:text-slate-200">{ministersInline(a.ministers)}</span>
        <DateRange started={a.started_on} ended={a.ended_on} />
      </div>
    </li>
  );
}

function ministersInline(ministers: MinisteringPersonRef[]): React.ReactNode {
  if (ministers.length === 0) return <span className="text-slate-400 dark:text-slate-500">(no ministers)</span>;
  return ministers.flatMap((m, i) => {
    const link = (
      <Link key={m.id} to={`/people/${m.id}`} className="hover:underline">
        {m.name_full ?? '(unnamed)'}
      </Link>
    );
    return i === 0 ? [link] : [<span key={`s${i}`} className="text-slate-400 dark:text-slate-500"> & </span>, link];
  });
}

function personRefsInline(refs: MinisteringPersonRef[]): React.ReactNode {
  return refs.flatMap((m, i) => {
    const link = (
      <Link key={m.id} to={`/people/${m.id}`} className="hover:underline">
        {m.name_full ?? '(unnamed)'}
      </Link>
    );
    return i === 0 ? [link] : [<span key={`s${i}`} className="text-slate-400 dark:text-slate-500"> & </span>, link];
  });
}

function DateRange({ started, ended }: { started: string; ended: string | null }) {
  return (
    <span className="text-xs text-slate-500 dark:text-slate-400">
      {started}
      {ended ? ` → ${ended}` : ' → present'}
    </span>
  );
}

// Used implicitly by JSX. Keep export to satisfy isolatedModules.
export type { PersonMinistering, HouseholdMinistering };
